/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, January 10th 2022, 4:32:56 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import { EventDirection, EventType, NODE_TYPES } from "lib/schemas";
import { InlineIcon, IconProps, IconifyIcon } from "@iconify/react";
import BPMNStartEventIcon from "@iconify/icons-bpmn/start-event";
import BPMNUserIcon from "@iconify/icons-bpmn/user";
import BPMNCallActivityICon from "@iconify/icons-bpmn/call-activity";
import BPMNGatewayIcon from "@iconify/icons-bpmn/gateway";
import BPMNGatewayParallelIcon from "@iconify/icons-bpmn/gateway-parallel";
import BPMNEndEventIcon from "@iconify/icons-bpmn/end-event";
import BPMNServiceTaskIcon from "@iconify/icons-bpmn/service";
import BPMNSignalIcon from "@iconify/icons-bpmn/intermediate-event";
import BPMNNonInterruptingCatchSignalIcon from "@iconify/icons-bpmn/intermediate-event-catch-non-interrupting-signal";
import BPMNInterruptingCatchSignalIcon from "@iconify/icons-bpmn/intermediate-event-catch-signal";
import BPMNNonInterruptingCatchTimerIcon from "@iconify/icons-bpmn/intermediate-event-catch-non-interrupting-timer";
import BPMNInterruptingCatchTimerIcon from "@iconify/icons-bpmn/intermediate-event-catch-timer";
import BPMNInterruptingCatchCompensationIcon from "@iconify/icons-bpmn/intermediate-event-catch-compensation";
import BPMNNonInterruptingCatchMessageIcon from "@iconify/icons-bpmn/intermediate-event-catch-non-interrupting-message";
import BPMNInterruptingCatchMessageIcon from "@iconify/icons-bpmn/intermediate-event-catch-message";
import BPMNNonInterruptingCatchEscalationIcon from "@iconify/icons-bpmn/intermediate-event-catch-non-interrupting-escalation";
import BPMNInterruptingCatchEscalationIcon from "@iconify/icons-bpmn/intermediate-event-catch-escalation";
import BPMNInterruptingCatchErrorIcon from "@iconify/icons-bpmn/intermediate-event-catch-error";
import BPMNThrowSignalIcon from "@iconify/icons-bpmn/intermediate-event-throw-signal";
import BPMNThrowCompensationIcon from "@iconify/icons-bpmn/intermediate-event-throw-compensation";
import BPMNThrowMessageIcon from "@iconify/icons-bpmn/intermediate-event-throw-message";
import BPMNThrowEscalationIcon from "@iconify/icons-bpmn/intermediate-event-throw-escalation";

export const BPMNIcon = ({ icon, ...props }) => <InlineIcon icon={icon} {...props} />;

export const AutoCreatedTaskTypes: Partial<keyof typeof NODE_TYPES>[] = ["StartFunction", "End"];

interface BPMNTaskTypeIconProps extends Omit<IconProps, "icon"> {
  taskType: keyof typeof NODE_TYPES;
}

export const BPMNTaskTypeIcon = ({ taskType, ...props }: BPMNTaskTypeIconProps) => {
  switch (taskType) {
    case "View":
      return <BPMNIcon className="os-user-task-icon" icon={BPMNUserIcon} {...props} />;
    case "Event":
      return <BPMNIcon className="os-call-activity-icon" icon={BPMNSignalIcon} {...props} />;
    case "Call":
      return <BPMNIcon className="os-call-activity-icon" icon={BPMNCallActivityICon} {...props} />;
    case "StartFunction":
      return <BPMNIcon className="os-start-event-icon" icon={BPMNStartEventIcon} {...props} />;
    case "Handler":
      return <BPMNIcon className="os-automated-task-icon" icon={BPMNServiceTaskIcon} {...props} />;
    case "If":
      return <BPMNIcon className="os-if-gateway-icon" icon={BPMNGatewayIcon} {...props} />;
    case "Job":
      return <BPMNIcon className="os-scheduled-task-icon" icon={BPMNServiceTaskIcon} {...props} />;
    case "Join":
      return <BPMNIcon className="os-merge-node-icon" icon={BPMNGatewayParallelIcon} {...props} />;
    case "Signal":
      return <BPMNIcon className="os-signal-node-icon" icon={BPMNSignalIcon} {...props} />;
    case "Split":
      return <BPMNIcon className="os-split-node-icon" icon={BPMNGatewayParallelIcon} {...props} />;
    case "Switch":
      return <BPMNIcon className="os-switch-node-icon" icon={BPMNGatewayIcon} {...props} />;
    case "End":
      return <BPMNIcon className="os-end-task-icon" icon={BPMNEndEventIcon} {...props} />;
    case "Function":
      return <BPMNIcon className="os-automated-task-icon" icon={BPMNCallActivityICon} {...props} />;
  }
};

const BPMNCatchEventIcons: {
  interrupring: Record<keyof typeof EventType, IconifyIcon>;
  nonInterrupring: Record<keyof typeof EventType, IconifyIcon>;
} = {
  interrupring: {
    Compensation: BPMNInterruptingCatchCompensationIcon,
    Error: BPMNInterruptingCatchErrorIcon,
    Escalation: BPMNInterruptingCatchEscalationIcon,
    Message: BPMNInterruptingCatchMessageIcon,
    Timer: BPMNInterruptingCatchTimerIcon,
    Signal: BPMNInterruptingCatchSignalIcon,
  },
  nonInterrupring: {
    Compensation: BPMNInterruptingCatchCompensationIcon,
    Error: BPMNInterruptingCatchErrorIcon, // Error is always interrupting
    Escalation: BPMNNonInterruptingCatchEscalationIcon,
    Message: BPMNNonInterruptingCatchMessageIcon,
    Timer: BPMNNonInterruptingCatchTimerIcon,
    Signal: BPMNNonInterruptingCatchSignalIcon,
  },
};

const BPMNThrowEventIcons: Record<keyof typeof EventType, IconifyIcon> = {
  Compensation: BPMNThrowCompensationIcon,
  Escalation: BPMNThrowEscalationIcon,
  Message: BPMNThrowMessageIcon,
  Signal: BPMNThrowSignalIcon,
  Timer: null, // Timer can only be caught
  Error: null, // Error can only be caught
};

interface BPMNEventIconProps extends Omit<IconProps, "icon"> {
  eventDirection: keyof typeof EventDirection;
  eventType: keyof typeof EventType;
  isInterrupting: boolean;
}

export const BPMNEventIcon = ({ eventDirection, eventType, isInterrupting, ...props }: BPMNEventIconProps) => {
  let className = "os-signal-node-icon";
  const IconOptions =
    eventDirection === "catch"
      ? isInterrupting
        ? BPMNCatchEventIcons.interrupring
        : BPMNCatchEventIcons.nonInterrupring
      : BPMNThrowEventIcons;
  let icon = IconOptions[eventType] ?? BPMNSignalIcon;
  return <BPMNIcon className={className} icon={icon} {...props} />;
};
